/**
 * 附件下载统一入口（2026-10-20 用户反馈「点击文件后系统打开新标签栏什么也不显示」）。
 *
 * 需求背景：
 *   设计部阶段会话 / 文件与版本弹窗里，文件名和下载图标原本用
 *   `<a href="/api/files/{id}/download" target="_blank">` 或 `window.open(...)` 打开。
 *   后端下载接口返回 `Content-Disposition: inline`（为了让 PDF / 图片能内嵌预览），
 *   于是浏览器对 zip / CAD / docx 这类无法内嵌渲染的文件只能「先下载、再留一个空白标签」，
 *   48MB 级别的大 PDF 也会让内嵌查看器长时间白屏。用户看到的就是「新标签页什么也没有」。
 *
 * 修复思路（为什么这样做）：
 *   · 前端域名（如 localhost:3000）与后端（localhost:8001 / api.aliadu.com）跨域时，
 *     `<a download>` 属性会被浏览器直接忽略（HTML 规范：跨域不允许指定下载名），
 *     所以必须由前端自己取回二进制再用 blob 触发下载，文件名才可控；
 *   · 统一走「fetch → Blob → 临时 <a download> 点击」，全程不新开标签页，
 *     下载完成即结束，不会再留下空白页；
 *   · 鉴权沿用现有 `?auth=<token>` 查询参数（下载接口既支持 Bearer 也支持 auth），
 *     避免 blob 拉取时还要额外拼请求头。
 */

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

/** 带登录态的文件下载地址。token 必须 encode：JWT 里的字符直接拼进 URL 会被截断。 */
export const fileDownloadUrl = (fileId, token) =>
  `${BACKEND}/api/files/${fileId}/download?auth=${encodeURIComponent(token || "")}`;

/**
 * 按 fileId 下载文件到本地（不新开标签页）。
 *
 * @param {string} fileId   后端 files 集合的 id（形如 Fxxxxxxxxxxx）
 * @param {string} filename 保存到本地时使用的文件名；缺省回退 file
 * @param {object} [opts]
 * @param {boolean} [opts.orig]  是否取原始文件（默认取压缩版；压缩版对 PDF 不影响可读性）
 * @param {boolean} [opts.jpeg] 图片强制取 JPEG（默认 true，见下）
 * @param {string}  [opts.token] 覆盖登录 token（一般不用传，默认读 authStore）
 * @returns {Promise<void>} 失败时抛出错误，由调用方决定是否 toast
 *
 * 为什么下载默认要 JPEG（2026-10-20 加）：
 *   图片压缩层改用 WebP 后，页面内预览拿到 WebP 是最清晰的（浏览器原生支持、省流量）。
 *   但「下载到本地」是另一回事 —— 用户往往是要拖进 Word / PPT / 微信 / 打印店，
 *   这些环境对 WebP 支持参差不齐（旧版 Office 直接不认，部分微信版本发出去是白块）。
 *   所以下载走 `?fmt=jpeg` 让服务端转一道 JPEG：体积略大一点点，但落地即用。
 *   确实要 WebP 原件的调用方传 `{ jpeg: false }`。
 *
 * 下载优先给「源文件」（2026-10-20 用户需求「下载时下载源文件」）：
 *   上传若是「先传预览、后台补原图」那条链路（`uploadChunked` 的 keepOriginal），
 *   files 上会挂 originalFileId。此时 `?src=1` 直接回原图，且**不要**再叠加 fmt=jpeg ——
 *   原图的定义就是「用户上传时的那个文件」，转码会把 HEIC 变 JPEG、把 12MB 变 2MB，
 *   那就不是源文件了。服务端对没挂源文件的文件会自动退回压缩版，所以这里可以无条件带 src=1。
 */
export async function downloadFile(fileId, filename = "file", opts = {}) {
  if (!fileId) throw new Error("missing fileId");
  let token = opts.token;
  if (token == null) {
    // 动态取 authStore：本模块会被弹窗 / 抽屉 / 灯箱同时引用，
    // 静态 import 在这几处都安全，但用动态 import 可以避免循环依赖（api.js → 各页面）。
    const { authStore } = await import("./api");
    token = authStore.get("ali_token") || "";
  }
  // 三种口径，优先级从「最保真」到「最兼容」：
  //   opts.orig  → 服务端最早存的那个原始字节（老语义，排障 / 要原图时用）
  //   默认       → 优先源文件(src=1)，没有则由服务端退回压缩版。源文件就是用户上传时
  //                的那个文件，**不叠加 fmt=jpeg** —— 否则 HEIC/12MB 会被转成 JPEG/2MB，
  //                那就不是"源文件"了
  //   jpeg:false → 只要压缩版，且不转码（要 WebP 原件的调用方）
  const wantJpeg = opts.jpeg !== false && !opts.orig;
  const url = fileDownloadUrl(fileId, token)
    + (opts.orig ? "&orig=1" : "&src=1")
    + (wantJpeg ? "&fmt=jpeg" : "");
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  // 服务端只在「确实是图片」时才转 JPEG，非图片会忽略 fmt 参数原样返回 ——
  // 所以只有确认拿到 image/jpeg 时才把扩展名改成 .jpg，否则保留原名（别把 PDF 存成 .jpg）。
  const savedName = /^image\/jpeg$/i.test(blob.type || "")
    ? (filename || "file").replace(/\.(webp|heic|heif|tiff?|avif)$/i, "") + (/(\.jpe?g)$/i.test(filename || "") ? "" : ".jpg")
    : (filename || "file");
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = savedName;
  a.rel = "noopener";
  // 必须挂到 document 上再 click：Firefox / 部分 Chromium 版本对游离节点的 click 不触发下载。
  document.body.appendChild(a);
  a.click();
  a.remove();
  // 60s 后再释放：部分浏览器（Safari）在大文件落盘前就撤销 URL 会导致下载中断。
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
}
